import { useEffect, useMemo } from "react";
import {
  ghostBtnClass,
  mfgInlineInputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type { VariantTransformRule } from "@/lib/manufacturing/variantConfig";
import { MaterialSearchableSelect } from "./MaterialSearchableSelect";
import {
  recipeMaterialOptionsForSlots,
  type RecipeLineMaterialSource,
} from "./namedVariantRecipeMaterialOptions";
import { SlotLabelsPicker } from "./SlotLabelsPicker";

function emptyTransformRule(): VariantTransformRule {
  return { slots: [], fromNominal: "", toNominal: "" };
}

function catalogIdentityCode(
  materials: readonly MfgMaterial[],
  raw: string,
): string | null {
  const needle = raw.trim().toLowerCase();
  if (!needle) return null;
  const byCode = materials.find((m) => m.code.trim().toLowerCase() === needle);
  if (byCode) return byCode.code;
  const byName = materials.find((m) => m.name.trim().toLowerCase() === needle);
  return byName ? byName.code : null;
}

function materialLabel(materials: readonly MfgMaterial[], code: string): string | null {
  const needle = code.trim().toLowerCase();
  if (!needle) return null;
  const hit = materials.find((m) => m.code.trim().toLowerCase() === needle);
  return hit ? `${hit.code} — ${hit.name}` : null;
}

/** Transform rules editor: from = recipe materials, to = catalog search; both store codes. */
export function ManufacturingNamedVariantApplicationTransformRules({
  rules,
  recipeLines,
  recipeLinesBusy,
  materials,
  orgSlotLabels,
  onChange,
}: {
  rules: readonly VariantTransformRule[];
  recipeLines: readonly RecipeLineMaterialSource[];
  recipeLinesBusy: boolean;
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  onChange: (next: VariantTransformRule[]) => void;
}) {
  const transformRules = rules.length > 0 ? rules : [emptyTransformRule()];

  function patch(index: number, patch: Partial<VariantTransformRule>) {
    onChange(transformRules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  return (
    <div className="space-y-2">
      {transformRules.map((rule, index) => (
        <TransformRuleRow
          key={index}
          rule={rule}
          index={index}
          canRemove={transformRules.length > 1}
          recipeLines={recipeLines}
          recipeLinesBusy={recipeLinesBusy}
          materials={materials}
          orgSlotLabels={orgSlotLabels}
          onPatch={(p) => patch(index, p)}
          onRemove={() => onChange(transformRules.filter((_, i) => i !== index))}
        />
      ))}
      <button
        type="button"
        className={ghostBtnClass}
        onClick={() => onChange([...transformRules, emptyTransformRule()])}
      >
        + Transform rule
      </button>
    </div>
  );
}

function TransformRuleRow({
  rule,
  index,
  canRemove,
  recipeLines,
  recipeLinesBusy,
  materials,
  orgSlotLabels,
  onPatch,
  onRemove,
}: {
  rule: VariantTransformRule;
  index: number;
  canRemove: boolean;
  recipeLines: readonly RecipeLineMaterialSource[];
  recipeLinesBusy: boolean;
  materials: readonly MfgMaterial[];
  orgSlotLabels: readonly string[];
  onPatch: (patch: Partial<VariantTransformRule>) => void;
  onRemove: () => void;
}) {
  const fromOptions = useMemo(
    () => recipeMaterialOptionsForSlots(recipeLines, rule.slots),
    [recipeLines, rule.slots],
  );

  useEffect(() => {
    if (recipeLinesBusy) return;
    const next: Partial<VariantTransformRule> = {};

    const fromRaw = rule.fromNominal.trim();
    const fromMatched = fromOptions.some(
      (o) => o.material_code.trim().toLowerCase() === fromRaw.toLowerCase(),
    );
    if (fromRaw && !fromMatched) {
      next.fromNominal = fromOptions.length === 1 ? fromOptions[0]!.material_code : "";
    } else if (!fromRaw && fromOptions.length === 1) {
      next.fromNominal = fromOptions[0]!.material_code;
    }

    const toRaw = rule.toNominal.trim();
    if (toRaw) {
      const bound = catalogIdentityCode(materials, toRaw);
      if (!bound) next.toNominal = "";
      else if (bound !== toRaw) next.toNominal = bound;
    }

    if (next.fromNominal === undefined && next.toNominal === undefined) return;
    if (
      (next.fromNominal === undefined || next.fromNominal === rule.fromNominal) &&
      (next.toNominal === undefined || next.toNominal === rule.toNominal)
    ) {
      return;
    }
    onPatch(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- clear/bind when recipe + catalog settle
  }, [recipeLinesBusy, fromOptions, materials, rule.fromNominal, rule.toNominal]);

  const fromSelectValue =
    fromOptions.find(
      (o) => o.material_code.trim().toLowerCase() === rule.fromNominal.trim().toLowerCase(),
    )?.material_code ?? "";

  const toLabel = materialLabel(materials, rule.toNominal);

  return (
    <div className="space-y-2 rounded border border-gray-100 p-2 dark:border-zinc-700">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-gray-500">Transform {index + 1}</span>
        <button type="button" className={ghostBtnClass} disabled={!canRemove} onClick={onRemove}>
          Remove
        </button>
      </div>
      <SlotLabelsPicker
        value={rule.slots.join(", ")}
        suggestions={orgSlotLabels}
        placeholder="Slot labels"
        onChange={(csv) =>
          onPatch({
            slots: csv
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean),
          })
        }
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <label className="block space-y-1">
          <span className="text-[11px] font-medium text-gray-600 dark:text-zinc-400">
            Change from
          </span>
          <select
            className={mfgInlineInputClass}
            value={fromSelectValue}
            disabled={recipeLinesBusy}
            onChange={(e) => onPatch({ fromNominal: e.target.value })}
          >
            <option value="">
              {recipeLinesBusy
                ? "Loading recipe materials…"
                : fromOptions.length === 0
                  ? "No recipe materials for slots"
                  : "Select recipe material…"}
            </option>
            {fromOptions.map((o) => (
              <option key={o.material_code} value={o.material_code}>
                {o.material_code} — {o.material_name}
              </option>
            ))}
          </select>
        </label>
        <label className="block space-y-1">
          <span className="text-[11px] font-medium text-gray-600 dark:text-zinc-400">
            Change to
          </span>
          <MaterialSearchableSelect
            materials={materials}
            placeholder="Search catalog by code or name…"
            onSelect={(m) => onPatch({ toNominal: m.code })}
          />
          {toLabel ? (
            <span className="block text-[10px] text-gray-500 dark:text-zinc-400">
              Selected: {toLabel}
            </span>
          ) : null}
        </label>
      </div>
    </div>
  );
}
